/* eslint-disable static-service/require-stories -- TODO: add stories */
/**
 * Renders math using MathJax v3. Use me like <MathJax>2x + 3</MathJax>.
 */
import * as React from "react";
import {useEffect, useMemo} from "react";
import arePropsEqual from "react-fast-compare";

import {
    selectableTextElement,
    swapSelectableElementForCopyableText,
} from "@khanacademy/mathjax-renderer";
import {useLatestRef, type StyleType} from "@khanacademy/wonder-blocks-core";
import Log, {Errors} from "@repo/logging";

import {AnalyticsEventEmitter} from "../analytics/analytics-event-emitter.ts";
import {selectionIntersectsNode} from "../analytics/selection.ts";
import {useCopyAnalytics} from "../analytics/use-copy-analytics.ts";

import {isPresentationalForSR} from "./is-presentational-for-sr.ts";
import {khanmigoTexKey} from "./khanmigo-tex-key.ts";
import {MountInSpan} from "./mount-in-span.tsx";
import {render, updateStyles} from "./render.ts";
import {wrapInDelimiters} from "./wrap-in-delimiters.ts";

export interface Props {
    /**
     * The TeX to render
     */
    children: string;

    /**
     * Called when the container element for the rendered math is clicked.
     */
    onClick?: () => unknown;

    /**
     * Called when the math elements have appeared onscreen. At this point, the
     * dimensions and layout of the math elements have already been set, so you
     * can e.g. measure the elements in this callback.
     *
     * Note that the math might not be human-readable yet when onRender is
     * called, for two reasons:
     * - Fonts might not have loaded in yet.
     * - The aria-label might not be set because the speech rule engine data
     *   hasn't loaded yet. MathJax elements don't always need an aria-label,
     *   since they have MathML which screenreaders can read in most cases.
     *   An aria-label is only necessary:
     *   - on ChromeOS, because ChromeVox doesn't read MathML in most
     *     circumstances.
     *   - when the math is inside an element with the `presentational` role.
     */
    onRender?: () => unknown;

    style?: StyleType;

    /**
     * Identifies the part of the product where the math is being rendered.
     */
    contextForAnalytics?: "ai-guide" | "content-library";

    /**
     * When true, MathJax rendering errors are not logged.
     */
    noLog?: boolean;
}

function MathJax(props: Props): React.ReactElement {
    const {children: tex, style, onRender, onClick, noLog} = props;
    const contextForAnalytics = props.contextForAnalytics ?? "content-library";

    const {domElement, normalizedTex, error, addLabel} = useMemo(
        () => render(tex),
        [tex],
    );
    const errorMessage = error?.message;

    useEffect(() => {
        // We can't useLocation here because MathJax might be used outside
        // AppShell and we don't want it to blow up.
        // eslint-disable-next-line no-restricted-syntax -- MathJax is used outside AppShell
        const urlPath = window.location.pathname;
        if (!noLog && errorMessage != null && !isDevPage(urlPath)) {
            logRenderingError(tex, errorMessage, contextForAnalytics);
        }
    }, [tex, errorMessage, noLog, contextForAnalytics]);

    useCopyableTex({
        normalizedTex,
        domElement,
    });

    useCopyAnalytics({
        tex,
        renderedDOMRef: {current: domElement},
        contextForAnalytics,
        renderer: "mathjax3",
    });

    // The MathJax stylesheet tries to include the bare minimum of
    // fonts and styles for the math that's on the page, so whenever we
    // render new math we need to tell MathJax to update the styles.
    useEffect(updateStyles, [tex]);

    const onRenderRef = useLatestRef(onRender);
    useEffect(() => {
        onRenderRef.current?.();
    }, [tex, onRenderRef]);

    // Add an aria-label attribute to the math element if it is mounted in a
    // context where screenreaders will not read the MathML.
    useEffect(() => {
        if (isChromebook() || isPresentationalForSR(domElement)) {
            addLabel();
        }
        // ChromeVox won't read aria-labels unless role is set. Furthermore,
        // not all roles cause the element to be read. I think "figure"
        // would be more appropriate than "application", but MathJax's own
        // accessibility tools use "application", and ChromeVox does not
        // read the aria-label if the role is "figure".
        if (isChromebook()) {
            domElement.setAttribute("role", "application");
        }
    }, [domElement, addLabel]);

    return (
        <>
            <AnalyticsEventEmitter
                context={contextForAnalytics}
                renderer="mathjax3"
            />
            <MountInSpan element={domElement} style={style} onClick={onClick} />
        </>
    );
}

const MemoizedMathJax = React.memo(MathJax, arePropsEqual);

export {MemoizedMathJax as MathJax};

// This copy-paste handling supports several use cases:
// 1. copying a single math element, pasting into a MathQuill editor
// 2. copying a single math element, pasting into Khanmigo
// 3. copying a single math element, pasting elsewhere (e.g. a text document)
// 4. copying multiple elements (including math and surrounding text), pasting
//    into Khanmigo
// 5. copying multiple elements (including math and surrounding text), pasting
//    elsewhere (e.g. a text document)
//
// Copying multiple elements into MathQuill is not supported; this is fine,
// since MathQuill is a math expression editor and can't/shouldn't handle text.
//
// Supporting all these cases is slightly complicated because Khanmigo requires
// delimiters `\[...\]` around TeX, while MathQuill forbids these delimiters,
// treating TeX as invalid if they are present. See the inline comments below
// for an explanation of how it works.
function useCopyableTex(params: {
    domElement: HTMLElement;
    normalizedTex: string;
}) {
    const {domElement, normalizedTex} = params;

    domElement.oncopy = (e) => {
        e.preventDefault();
        const data = e.clipboardData;
        if (data == null) {
            return;
        }
        // To support use cases (1, 2, 3) simultaneously, we put the
        // undelimited TeX on the clipboard as text data for MathQuill and
        // plain text editors. We also copy the delimited TeX as a special
        // content type which Khanmigo will recognize.
        data.setData("text/plain", normalizedTex);
        data.setData(khanmigoTexKey, wrapInDelimiters(normalizedTex));
    };

    useEffect(() => {
        // To support use cases (4, 5), we fill the selectable math elements
        // with delimited TeX, just before the browser copies the content to
        // the clipboard. swapSelectableElementForCopyableText will clear the
        // selectable elements just after the copy operation completes. We need
        // to do this because the selectable math elements are empty most of
        // the time, to prevent TeX from being revealed by ctrl+F search.
        function onCopy() {
            if (selectionIntersectsNode(selectableTextElement(domElement))) {
                swapSelectableElementForCopyableText(
                    domElement,
                    wrapInDelimiters(normalizedTex),
                );
            }
        }

        document.body.addEventListener("copy", onCopy);
        return () => document.body.removeEventListener("copy", onCopy);
    }, [domElement, normalizedTex]);
}

function isDevPage(path: string): boolean {
    return (
        path.startsWith("/devadmin/") ||
        path.startsWith("/perseus/exercise-frame") ||
        path.startsWith("/perseus/frame")
    );
}

// TODO(benchristel): duplicated code. Move to a new package
// libs/util/user-agent
function isChromebook() {
    return /CrOS/.test(navigator.userAgent);
}

function logRenderingError(
    tex,
    errorMessage: string,
    contextForAnalytics: string,
) {
    Log.error("MathJax rendering error", Errors.Internal, {
        sentryData: {
            contexts: {
                mathjax: {
                    tex,
                    error: errorMessage,
                    source: contextForAnalytics,
                },
            },
        },
    });
}
